import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { MinimalTiptapEditor } from '@/components/ui/minimal-tiptap/minimal-tiptap'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import { useTeam } from 'app/frontend/base/config/context/team'
import { uploadFile } from 'app/frontend/base/api/team'

export const hasNotesContent = (html: string | undefined | null): boolean => {
  if (!html) return false
  if (/<(img|iframe|video|audio|embed)\b/i.test(html)) return true
  return html.replace(/<[^>]*>/g, '').trim().length > 0
}

type Props = {
  initialValue: string
  onSave: (notes: string) => Promise<void>
  onCancel: () => void
  clearTarget?: string
}

const NotesEditor = ({
  initialValue,
  onSave,
  onCancel,
  clearTarget = 'this event',
}: Props) => {
  const { team } = useTeam()
  const uploader = async (file: File) => (await uploadFile(team.id, file)).url
  const [draft, setDraft] = useState(initialValue)
  const [isSaving, setIsSaving] = useState(false)
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false)

  const hasSavedNotes = hasNotesContent(initialValue)

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await onSave(draft)
    } finally {
      setIsSaving(false)
    }
  }

  const handleClear = async () => {
    setIsSaving(true)
    try {
      await onSave('')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <div className='flex flex-col overflow-hidden rounded-lg border bg-card shadow-2xs'>
        <MinimalTiptapEditor
          value={draft}
          onChange={(value) => setDraft(typeof value === 'string' ? value : '')}
          output='html'
          placeholder='Add message…'
          uploader={uploader}
          className='rounded-none border-0 shadow-none'
          editorContentClassName='min-h-32'
        />
        <div className='flex gap-2 justify-end border-t p-2'>
          {hasSavedNotes ? (
            <Button
              variant='ghost'
              size='sm'
              className='hover:text-destructive-foreground'
              disabled={isSaving}
              onClick={() => setIsClearConfirmOpen(true)}
            >
              Clear
            </Button>
          ) : null}
          <Button
            variant='outline'
            size='sm'
            disabled={isSaving}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            size='sm'
            loading={isSaving}
            onClick={() => void handleSave()}
          >
            Save
          </Button>
        </div>
      </div>
      <ConfirmDialog
        open={isClearConfirmOpen}
        title='Clear notes?'
        description={`This will permanently remove the notes from ${clearTarget}.`}
        variant='destructive'
        onCancel={() => setIsClearConfirmOpen(false)}
        onContinue={() => void handleClear()}
      />
    </>
  )
}

export default NotesEditor
